import React, { createContext, useContext, useEffect, useState } from 'react';

const SwitchContext = createContext<{
  value: string | null;
  setValue: React.Dispatch<React.SetStateAction<string | null>>;
  name: string;
} | null>(null);

let switchCounter = 0;

interface SwitchProps {
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  'aria-label'?: string;
}

export function Switch({ children, size = 'medium', className = '', 'aria-label': ariaLabel }: SwitchProps) {
  const [value, setValue] = useState<string | null>(null);
  const [name] = useState(() => `switch-${++switchCounter}`);

  return (
    <SwitchContext.Provider value={{ value, setValue, name }}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className={`flex bg-white/[0.04] p-1 border border-white/[0.08] ${
          size === 'small' ? 'h-8 rounded-md' :
          size === 'medium' ? 'h-10 rounded-md' :
          'h-12 rounded-lg'
        } ${className}`}
      >
        {React.Children.map(children, (child) =>
          React.isValidElement<SwitchControlProps>(child)
            ? React.cloneElement(child, { size })
            : child
        )}
      </div>
    </SwitchContext.Provider>
  );
}

interface SwitchControlProps {
  label?: string;
  value: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  icon?: React.ReactNode;
  onChange?: (value: string) => void;
}

function SwitchControl({
  label,
  value,
  defaultChecked,
  disabled = false,
  size = 'medium',
  icon,
  onChange,
}: SwitchControlProps) {
  const context = useContext(SwitchContext);
  const checked = value === context?.value;
  const inputId = context ? `${context.name}-${value}` : value;

  useEffect(() => {
    if (defaultChecked && context) {
      context.setValue(value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange() {
    if (disabled) return;
    context?.setValue(value);
    onChange?.(value);
  }

  return (
    <label
      htmlFor={inputId}
      className={`flex flex-1 h-full ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
    >
      <input
        id={inputId}
        type="radio"
        name={context?.name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        className="sr-only"
      />
      <span
        className={`flex items-center justify-center flex-1 font-mono font-medium transition-all duration-200 tracking-wide uppercase ${
          checked
            ? 'bg-white/[0.10] text-white/90 rounded-[5px] shadow-[0_0_8px_rgba(255,255,255,0.04)]'
            : 'text-white/35 hover:text-white/55'
        } ${
          icon
            ? size === 'small' ? 'py-1 px-2' : size === 'medium' ? 'py-2 px-3' : 'p-3'
            : size === 'small' ? 'text-[11px] px-3' : size === 'medium' ? 'text-[12px] px-3' : 'text-sm px-4'
        }`}
      >
        {icon ? <span className={size === 'large' ? 'scale-125' : ''}>{icon}</span> : label}
      </span>
    </label>
  );
}

Switch.Control = SwitchControl;
